import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The Study Helper Tutorials shell: the archive and the single post.
 *
 * Legacy's gate is a blocklist: it refuses signed-out visitors, every kind of
 * student, and follow-up partners and coordinators. In v3 that leaves study
 * helpers (including the former explorer type) and admins, which is the
 * server's requirement.
 *
 * The server refuses with 403 (401 when signed out) where legacy returned 404,
 * so this shell renders the not-found screen. It closes the web audience only:
 * the fullapp study helper tutorial endpoints accept any signed-in user, as
 * legacy's controller did.
 */
function StudyHelperTutorialsLayout(): ReactElement | null {
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, not `useCurrentUser().isStudyHelper`, which also
  // includes the follow-up roles legacy's blocklist names.
  const allowed =
    me?.profile_type === 'STUDY_HELPER' ||
    // Admins pass the server's requirement, so a gate without them would render
    // NotFoundScreen over a working response.
    me?.profile_type === 'ADMIN';

  // `/users/me` is pending on first paint, and a bare `!allowed` bounces every
  // caller, study helpers included.
  if (isPending) return null;
  if (!allowed) return <NotFoundScreen />;

  return <Outlet />;
}

export const Route = createFileRoute('/_app/study-helper-tutorials')({
  component: StudyHelperTutorialsLayout,
});
